import { useState, useEffect, useRef, useMemo } from 'react';
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GetAppIcon from "@mui/icons-material/GetApp";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import WorkIcon from "@mui/icons-material/Work";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { postRightworkAPI } from "../requests/utilities/requests";
import { formatDateReadable } from "../requests/utilities/helpers";
import { API_post_pay_period_tip_amounts } from "../../../constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  maxWidth: "95vw",
  maxHeight: "99vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

// Currency formatting function
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper to format cents as currency
const formatCurrencyFromCents = (cents) => {
  return formatCurrency((cents || 0) / 100);
};

export default function TipCalculatorModal({
  open = true,
  onClose = () => {},
  payPeriodEmployees,
  selectedPayPeriodDates,
  platformTipTotalCents,
  creditCardTipAmountCents,
  cashTipAmountCents,
  tipList = [],
  selectedPayPeriodUUID,
  homeStore,
  onSubmitSuccess = () => {}
}) {
  // Filter out employees with 0 hours at the start
  const workingEmployees = payPeriodEmployees?.filter(emp =>
    (emp.timecard_summary_data?.payable_hours || 0) > 0
  ) || [];

  console.log("PLATFORM", platformTipTotalCents)
  console.log("CREDIT", creditCardTipAmountCents)
  console.log("CASH", cashTipAmountCents)

  // Credit card tips state - now tracks the final amount separately
  const [adjustCreditCardTips, setAdjustCreditCardTips] = useState(false);
  const [creditCardTipsInput, setCreditCardTipsInput] = useState('0');
  const [creditCardTipsFromSystem, setCreditCardTipsFromSystem] = useState(0);
  const [finalCreditCardTips, setFinalCreditCardTips] = useState(0); // The amount to actually use

  // Cash tips state
  const [adjustCashTips, setAdjustCashTips] = useState(false);
  const [cashTipsInput, setCashTipsInput] = useState('0');

  // Update state when props change
  useEffect(() => {
    if (platformTipTotalCents != null) {
      setCreditCardTipsFromSystem(platformTipTotalCents);
    }
    if (creditCardTipAmountCents != null) {
      // Convert cents to dollars for input display
      setCreditCardTipsInput((creditCardTipAmountCents / 100).toFixed(2));
      setFinalCreditCardTips(creditCardTipAmountCents);
    }
    if (cashTipAmountCents != null) {
      // Convert cents to dollars for input display
      setCashTipsInput((cashTipAmountCents / 100).toFixed(2));
    }
  }, [platformTipTotalCents, creditCardTipAmountCents, cashTipAmountCents]);

  // Other existing state
  const [hasTipOut, setHasTipOut] = useState(false);
  const [tipOutPercentage, setTipOutPercentage] = useState('10');
  const [excludedEmployees, setExcludedEmployees] = useState(new Set());

  // Role filtering state
  const [excludedRoles, setExcludedRoles] = useState(new Set());
  const [isRolesExpanded, setIsRolesExpanded] = useState(false);

  // Track if we've initialized excluded employees/roles to avoid resetting user changes
  const hasInitializedExcluded = useRef(false);
  const hasInitializedExcludedRoles = useRef(false);

  // Extract all unique roles from all employees
  const allRoles = useMemo(() => {
    const rolesMap = new Map();
    payPeriodEmployees?.forEach(emp => {
      emp.hours_by_role?.forEach(roleData => {
        const key = roleData.role_id ?? 'unassigned';
        if (!rolesMap.has(key)) {
          rolesMap.set(key, {
            id: roleData.role_id,
            name: roleData.role_name,
            tip_eligible: roleData.tip_eligible
          });
        }
      });
    });
    return Array.from(rolesMap.values());
  }, [payPeriodEmployees]);

  // Initialize excluded roles based on tip_eligible flag (only once when modal opens)
  useEffect(() => {
    if (open && !hasInitializedExcludedRoles.current && allRoles.length > 0) {
      // Auto-exclude roles where tip_eligible is false
      const nonTipEligibleRoles = new Set(
        allRoles
          .filter(role => role.tip_eligible === false)
          .map(role => role.id ?? 'unassigned')
      );
      setExcludedRoles(nonTipEligibleRoles);
      hasInitializedExcludedRoles.current = true;
    }

    // Reset when modal closes
    if (!open) {
      hasInitializedExcludedRoles.current = false;
    }
  }, [open, allRoles]);

  // Helper function to calculate tip-eligible hours for an employee
  const calculateTipEligibleHours = (employee) => {
    // If no role breakdown available, use total hours (backward compatibility)
    if (!employee.hours_by_role || employee.hours_by_role.length === 0) {
      return employee.timecard_summary_data?.payable_hours || 0;
    }

    // Sum hours from non-excluded roles
    return employee.hours_by_role
      .filter(roleData => {
        const roleKey = roleData.role_id ?? 'unassigned';
        return !excludedRoles.has(roleKey);
      })
      .reduce((sum, roleData) => sum + roleData.hours, 0);
  };

  // Handler for role exclusion toggle
  const handleRoleExclusionToggle = (roleKey) => {
    const newExcluded = new Set(excludedRoles);
    if (newExcluded.has(roleKey)) {
      newExcluded.delete(roleKey);
    } else {
      newExcluded.add(roleKey);
    }
    setExcludedRoles(newExcluded);
  };

  // Initialize excluded employees based on tipList (only once when modal opens)
  useEffect(() => {
    if (open && !hasInitializedExcluded.current && workingEmployees.length > 0) {
      if (tipList.length > 0) {
        // Create a set of employee IDs that are in the tipList (included last time)
        const includedEmployeeIds = new Set(
          tipList.map(tip => tip.store_team_member?.id).filter(id => id != null)
        );

        // Find employees with hours who are NOT in the tipList (excluded last time)
        const excluded = new Set();
        workingEmployees.forEach(emp => {
          if (!includedEmployeeIds.has(emp.id)) {
            excluded.add(emp.id);
          }
        });

        setExcludedEmployees(excluded);
      }
      hasInitializedExcluded.current = true;
    }

    // Reset when modal closes
    if (!open) {
      hasInitializedExcluded.current = false;
    }
  }, [open, workingEmployees, tipList]);

  // Calculate total tips - use finalCreditCardTips when not adjusting, input when adjusting
  // Note: inputs are in dollars, so convert to cents for calculations
  const creditCardTips = adjustCreditCardTips ? Math.round((Number(creditCardTipsInput) || 0) * 100) : finalCreditCardTips;
  const cashTips = Math.round((Number(cashTipsInput) || 0) * 100);
  const totalTips = creditCardTips + cashTips;

  const handleCreditCardTipsToggle = (e) => {
    const isChecked = e.target.checked;

    if (!isChecked) {
      // When turning off the switch, save the current input as the final amount (convert dollars to cents)
      setFinalCreditCardTips(Math.round((Number(creditCardTipsInput) || 0) * 100));
    }

    setAdjustCreditCardTips(isChecked);
  };

  const handleCreditCardTipsChange = (e) => {
    const value = e.target.value;
    setCreditCardTipsInput(value);
  };

  const handleCashTipsToggle = (e) => {
    setAdjustCashTips(e.target.checked);
  };

  const handleCashTipsChange = (e) => {
    const value = e.target.value;
    setCashTipsInput(value);
  };

  const handleTipOutToggle = (e) => {
    setHasTipOut(e.target.checked);
  };

  const handleTipOutPercentageChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d*\.?\d*$/.test(value) && Number(value) <= 100)) {
      setTipOutPercentage(value);
    }
  };

  const handleEmployeeExclusionToggle = (employeeId) => {
    const newExcluded = new Set(excludedEmployees);
    if (newExcluded.has(employeeId)) {
      newExcluded.delete(employeeId);
    } else {
      newExcluded.add(employeeId);
    }
    setExcludedEmployees(newExcluded);
  };

  // Calculate tip distribution
  const tipOutAmount = hasTipOut ? totalTips * (Number(tipOutPercentage) / 100) : 0;
  const tipsAfterTipOut = totalTips - tipOutAmount;

  // Filter out excluded employees for calculations
  const activeEmployees = workingEmployees?.filter(emp => !excludedEmployees.has(emp.id)) || [];
  // Use role-filtered hours for total calculation
  const totalHours = activeEmployees.reduce((sum, emp) => sum + calculateTipEligibleHours(emp), 0);
  const tipsPerHour = totalHours > 0 ? tipsAfterTipOut / totalHours : 0;

  // Calculate individual tips and ensure they sum to total
  const employeesWithTips = workingEmployees?.map((emp, index) => {
    // Use role-filtered hours for each employee
    const hours = calculateTipEligibleHours(emp);
    const isExcluded = excludedEmployees.has(emp.id);
    const calculatedTips = isExcluded ? 0 : hours * tipsPerHour;

    return {
      ...emp,
      hours: hours,
      totalHours: emp.timecard_summary_data?.payable_hours || 0, // Keep original total for display
      tips: calculatedTips,
      tipRate: isExcluded ? 0 : tipsPerHour,
      isExcluded: isExcluded
    };
  });

  // Adjust for rounding to ensure total matches (only for non-excluded employees)
  const activeEmployeesWithTips = employeesWithTips.filter(emp => !emp.isExcluded);
  const calculatedTotal = activeEmployeesWithTips.reduce((sum, emp) => sum + emp.tips, 0);
  const roundingAdjustment = tipsAfterTipOut - calculatedTotal;

  // Apply adjustment to the non-excluded employee with the most hours
  if (activeEmployeesWithTips.length > 0) {
    const maxHoursIndex = activeEmployeesWithTips.reduce((maxIndex, emp, index) =>
      emp.hours > activeEmployeesWithTips[maxIndex].hours ? index : maxIndex, 0
    );

    const originalEmployeeIndex = employeesWithTips.findIndex(emp =>
      emp.id === activeEmployeesWithTips[maxHoursIndex].id
    );

    if (originalEmployeeIndex >= 0) {
      employeesWithTips[originalEmployeeIndex].tips += roundingAdjustment;
    }
  }

  const handleSubmit = async () => {
    try {
      toast.loading("Submitting tips...", { autoClose: false });

      // Build the pay_period_approvals_with_tips_list from employeesWithTips
      // Only include employees who are NOT excluded
      const approvalsWithTips = employeesWithTips
        .filter(emp => !emp.isExcluded)
        .map(emp => ({
          stm_id: emp.id,
          total_tip_amount_cents: Math.round(emp.tips) // Round to nearest cent
        }));

      const requestData = {
        pay_period_uuid: selectedPayPeriodUUID,
        store_id: homeStore,
        manually_entered_cash_tip_amount_cents: Math.round(cashTips),
        manually_entered_credit_card_tip_amount_cents: Math.round(creditCardTips),
        pay_period_approvals_with_tips_list: JSON.stringify(approvalsWithTips)
      };

      const res = await postRightworkAPI(API_post_pay_period_tip_amounts, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Tips submitted successfully", { autoClose: 3000 });
        onSubmitSuccess(); // Trigger refetch of data
        onClose();
      }
    } catch (error) {
      toast.dismiss();
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to submit tips", { autoClose: 3000 });
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="tip-calculator-modal-title"
      aria-describedby="tip-calculator-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-10 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <AttachMoneyIcon className="text-violet-600" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900" id="tip-calculator-modal-title">
                  Tip Calculator
                </h3>
                <p className="text-sm text-slate-600">
                  Distribute tips fairly across your team based on hours worked
                </p>
              </div>
            </div>
            <IconButton
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className="px-6 space-y-6">
          {/* Summary Cards */}
          <div className={`grid ${hasTipOut ? 'grid-cols-4' : 'grid-cols-3'} gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-200`}>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AttachMoneyIcon className="text-violet-700" style={{ fontSize: "18px" }} />
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">Total Tips</p>
              </div>
              <p className="text-3xl font-bold text-gray-900">{formatCurrencyFromCents(totalTips)}</p>
            </div>
            {hasTipOut && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <RestaurantIcon className="text-orange-600" style={{ fontSize: "18px" }} />
                  <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">Tip Out</p>
                </div>
                <p className="text-3xl font-bold text-orange-600">{formatCurrencyFromCents(tipOutAmount)}</p>
              </div>
            )}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AccessTimeIcon className="text-violet-700" style={{ fontSize: "18px" }} />
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">Tipped Hours</p>
              </div>
              <p className="text-3xl font-bold text-gray-900">{totalHours?.toLocaleString("en-US",{ minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CalendarTodayIcon className="text-violet-700" style={{ fontSize: "18px" }} />
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">Tips per Hour</p>
              </div>
              <p className="text-3xl font-bold text-gray-900">{formatCurrencyFromCents(tipsPerHour)}</p>
            </div>
          </div>

          {/* Input Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pay Period Settings</h2>

            {/* Pay Period Display */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pay Period
              </label>
              <div className="flex items-center h-11 px-4 bg-gray-50 rounded-lg border border-gray-200">
                <CalendarTodayIcon className="text-gray-500 mr-2" style={{ fontSize: "18px" }} />
                <span className="text-sm font-medium text-gray-700">
                  {selectedPayPeriodDates && selectedPayPeriodDates.length === 2 ?
                    `${formatDateReadable(selectedPayPeriodDates[0])} to ${formatDateReadable(selectedPayPeriodDates[1])}` :
                    'No pay period selected'
                  }
                </span>
              </div>
            </div>

            {/* Tip Sources */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Tip Sources</h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Credit Card Tips Section */}
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <CreditCardIcon className="text-gray-500" style={{ fontSize: "20px" }} />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={adjustCreditCardTips}
                            onChange={handleCreditCardTipsToggle}
                            color="primary"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#7c3aed',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#7c3aed',
                              },
                            }}
                          />
                        }
                        label="Adjust credit card tips"
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: "14px",
                            fontWeight: 500,
                            color: '#374151'
                          }
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-600">
                      System pulled: {formatCurrencyFromCents(creditCardTipsFromSystem)}
                    </div>
                  </div>

                  {adjustCreditCardTips ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Credit Card Tips ({formatCurrency(0).slice(0, 1)})
                      </label>
                      <TextField
                        type="text"
                        value={creditCardTipsInput}
                        onChange={handleCreditCardTipsChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={(creditCardTipsFromSystem / 100).toFixed(2)}
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '[0-9]*\\.?[0-9]*'
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            height: "45px",
                            fontSize: "16px",
                            fontWeight: "600",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#7c3aed",
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Current credit card tips:</div>
                      <div className="text-lg font-bold text-gray-900">{formatCurrencyFromCents(finalCreditCardTips)}</div>
                    </div>
                  )}
                </div>

                {/* Cash Tips Section */}
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                  <div className="flex items-center gap-3 mb-4">
                    <LocalAtmIcon className="text-gray-500" style={{ fontSize: "20px" }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={adjustCashTips}
                          onChange={handleCashTipsToggle}
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#7c3aed',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#7c3aed',
                            },
                          }}
                        />
                      }
                      label="Adjust cash tips"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: "14px",
                          fontWeight: 500,
                          color: '#374151'
                        }
                      }}
                    />
                  </div>

                  {adjustCashTips ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cash Tips ({formatCurrency(0).slice(0, 1)})
                      </label>
                      <TextField
                        type="text"
                        value={cashTipsInput}
                        onChange={handleCashTipsChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="0.00"
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '[0-9]*\\.?[0-9]*'
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            height: "45px",
                            fontSize: "16px",
                            fontWeight: "600",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#7c3aed",
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Current cash tips:</div>
                      <div className="text-lg font-bold text-gray-900">{formatCurrencyFromCents(cashTips)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tip Out Settings */}
            {/* <div className="mt-4">
              <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <RestaurantIcon className="text-gray-500" style={{ fontSize: "20px" }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hasTipOut}
                          onChange={handleTipOutToggle}
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#7c3aed',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#7c3aed',
                            },
                          }}
                        />
                      }
                      label="Enable Tip Out (e.g. Kitchen / BOH)"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: "14px",
                          fontWeight: 500,
                          color: '#374151'
                        }
                      }}
                    />
                  </div>
                  {hasTipOut && (
                    <div className="flex items-center gap-2">
                      <TextField
                        type="text"
                        value={tipOutPercentage}
                        onChange={handleTipOutPercentageChange}
                        size="small"
                        placeholder="10"
                        inputProps={{
                          inputMode: 'numeric',
                          style: { width: '60px' }
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: "white",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#7c3aed",
                            },
                          },
                        }}
                      />
                      <span className="text-sm font-medium text-gray-700">%</span>
                    </div>
                  )}
                </div>
              </div>
            </div> */}
          </div>

          {/* Role Filter Section */}
          {allRoles.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsRolesExpanded(!isRolesExpanded)}
              >
                <div className="flex items-center gap-2">
                  <WorkIcon className="text-violet-700" style={{ fontSize: "20px" }} />
                  <h2 className="text-lg font-semibold text-gray-800">Filter by Role</h2>
                  {excludedRoles.size > 0 && (
                    <span className="ml-2 px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                      {excludedRoles.size} excluded
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {isRolesExpanded ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  {isRolesExpanded ? (
                    <ExpandLessIcon className="text-gray-400" />
                  ) : (
                    <ExpandMoreIcon className="text-gray-400" />
                  )}
                </div>
              </div>

              {isRolesExpanded && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-500 mb-3">
                    Uncheck roles to exclude their hours from tip calculation
                  </p>
                  {allRoles.map((role) => {
                    const roleKey = role.id ?? 'unassigned';
                    const isExcluded = excludedRoles.has(roleKey);
                    // Calculate total hours for this role across active employees
                    const roleHours = activeEmployees.reduce((sum, emp) => {
                      const roleData = emp.hours_by_role?.find(r =>
                        (r.role_id ?? 'unassigned') === roleKey
                      );
                      return sum + (roleData?.hours || 0);
                    }, 0);

                    return (
                      <div
                        key={roleKey}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isExcluded ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!isExcluded}
                            onChange={() => handleRoleExclusionToggle(roleKey)}
                            size="small"
                            sx={{
                              color: '#7c3aed',
                              '&.Mui-checked': { color: '#7c3aed' },
                            }}
                          />
                          <span className={isExcluded ? 'text-gray-400' : 'text-gray-700'}>
                            {role.name}
                          </span>
                          {!role.tip_eligible && (
                            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded">
                              Not tip eligible
                            </span>
                          )}
                        </div>
                        <span className={`text-sm ${isExcluded ? 'text-gray-400' : 'text-gray-600'}`}>
                          {roleHours.toFixed(2)} hrs
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Employee Table */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PeopleIcon className="text-violet-700" style={{ fontSize: "20px" }} />
                <h2 className="text-lg font-semibold text-gray-800">Employee Tip Distribution</h2>
              </div>
              <p className="text-sm text-gray-500">
                Uncheck employees to exclude from tip distribution
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wide">
                      <div className="flex items-center gap-2">
                        Include
                        <span className="text-gray-600">|</span>
                        Employee Name
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-900 uppercase tracking-wide">
                      Hours Worked
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-900 uppercase tracking-wide">
                      Tip Rate
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-900 uppercase tracking-wide">
                      Calculated Tips
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employeesWithTips.map((employee, index) => (
                    <tr key={employee.id || index} className={`transition-colors ${employee.isExcluded ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!employee.isExcluded}
                            onChange={() => handleEmployeeExclusionToggle(employee.id)}
                            size="small"
                            sx={{
                              color: '#7c3aed',
                              '&.Mui-checked': {
                                color: '#7c3aed',
                              },
                            }}
                          />
                          <span className={employee.isExcluded ? 'text-gray-500' : 'text-gray-900'}>
                            {employee.full_name}
                          </span>
                          {employee.isExcluded && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                              Excluded
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">
                        <span className={`px-3 py-1 rounded-full font-medium ${employee.isExcluded ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-700'}`}>
                          {employee.hours?.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )} hrs
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">
                        {employee.isExcluded ? (
                          <span className="px-3 py-1 rounded-full font-medium bg-gray-200 text-gray-500">
                            {formatCurrencyFromCents(employee.tipRate)}/hr
                          </span>
                        ) : (
                          <span className="font-medium text-gray-700">
                            {formatCurrencyFromCents(employee.tipRate)}/hr
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-lg font-bold">
                        <span className={employee.isExcluded ? 'text-gray-400' : 'text-gray-900'}>
                          {formatCurrencyFromCents(employee.tips)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-500">
                  <tr>
                    <td className="px-6 py-4 text-sm font-bold text-gray-900 uppercase">
                      Total Distribution
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                      {totalHours?.toLocaleString("en-US",{ minimumFractionDigits: 2, maximumFractionDigits: 2 })} hrs
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                      {formatCurrencyFromCents(tipsPerHour)}/hr
                    </td>
                    <td className="px-6 py-4 text-right text-lg font-bold text-gray-900">
                      {formatCurrencyFromCents(employeesWithTips.reduce((sum, emp) => sum + emp.tips, 0))}
                    </td>
                  </tr>
                  {hasTipOut && (
                    <>
                      <tr>
                        <td className="px-6 py-4 text-sm font-bold text-orange-600 uppercase">
                          Tip Out ({tipOutPercentage}%)
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-orange-600">
                          —
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-orange-600">
                          —
                        </td>
                        <td className="px-6 py-4 text-right text-lg font-bold text-orange-600">
                          {formatCurrencyFromCents(tipOutAmount)}
                        </td>
                      </tr>
                      <tr className="bg-gray-100">
                        <td className="px-6 py-4 text-sm font-bold text-gray-900 uppercase">
                          Grand Total
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                          —
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                          —
                        </td>
                        <td className="px-6 py-4 text-right text-lg font-bold text-gray-900">
                          {formatCurrencyFromCents(totalTips)}
                        </td>
                      </tr>
                    </>
                  )}
                </tfoot>
              </table>
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10 rounded-b-2xl">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <GetAppIcon style={{ fontSize: "18px" }} />
              Submit Tips
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
